import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Button,
  CircularProgress,
  Stack,
  Link,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  Warning,
  Verified,
  Info,
  ExpandMore,
  Person,
  Business,
  FilterList,
  Undo,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  monitoredIdentityService,
  mentionService,
  MentionDto, 
  MentionMatchType, 
  MentionFeedbackDecision,
  MonitoredIdentityDto,
  NationalIdDto,
  PublicationDto,
  IdType
} from '../services';
import { formatNationalId } from '../utils/nationalIdFormatter';

interface MentionCardProps {
  mention: MentionDto;
  mentionIdentity: MonitoredIdentityDto | null;
  isExpanded: boolean;
  feedbackLoading: boolean;
  onToggleExpansion: (mentionId: number) => void;
  onFeedback: (mentionId: number, decision: MentionFeedbackDecision) => void;
  onUndoFeedback: (mentionId: number) => void;
  expandButtonRef: (el: HTMLElement | null) => void;
  t: (key: string, options?: any) => string;
  i18nLanguage: string;
}

// Helper function for display name computation from NationalIdDto
const getNationalIdDisplayName = (nationalId: NationalIdDto, t: (key: string) => string): string => {
  if (nationalId.fullName) {
    return nationalId.fullName;
  }
  // Compute from parts if fullName not available
  const nameParts = [
    nationalId.firstName,
    nationalId.middleNames,
    nationalId.lastName1,
    nationalId.lastName2
  ].filter(Boolean);
  return nameParts.length > 0 ? nameParts.join(' ') : t('mentions.unknownName');
};

// Helper function for context truncation with matched text awareness
const truncateContext = (context: string | undefined, matchedText: string, maxChars: number = 30) => {
  if (!context || context.length <= maxChars * 2 + matchedText.length) {
    return { text: context || '', wasTruncated: false };
  }
  
  const matchIndex = context.indexOf(matchedText);
  if (matchIndex === -1) {
    return { 
      text: context.substring(0, maxChars * 2), 
      wasTruncated: true 
    };
  }
  
  const start = Math.max(0, matchIndex - maxChars);
  const end = Math.min(context.length, matchIndex + matchedText.length + maxChars);
  const prefix = start > 0 ? '...' : '';
  const suffix = end < context.length ? '...' : '';
  
  return {
    text: prefix + context.substring(start, end) + suffix,
    wasTruncated: true
  };
};

// Helper function for source URL with page parameter
const formatSourceUrl = (publication: PublicationDto, pageNumber?: number): string => {
  if (!publication.sourceUrl) return '#';
  return pageNumber 
    ? `${publication.sourceUrl}#page=${pageNumber}`
    : publication.sourceUrl;
};

// Helper function for icon color based on mention state
const getIconColor = (
  isCollapsed: boolean,
  userFeedback: { decision: MentionFeedbackDecision } | null | undefined,
  isNameHeuristic: boolean
): string => {
  if (isCollapsed) {
    return 'action.disabled';
  }
  
  if (userFeedback?.decision === MentionFeedbackDecision.Rejected) {
    return 'action.disabled';
  }
  
  if (userFeedback?.decision === MentionFeedbackDecision.Accepted || !isNameHeuristic) {
    return 'success.main';
  }
  
  return 'warning.main';
};


const MentionCard = React.memo<MentionCardProps>(({ 
  mention, 
  mentionIdentity, 
  isExpanded, 
  feedbackLoading, 
  onToggleExpansion, 
  onFeedback, 
  onUndoFeedback,
  expandButtonRef,
  t,
  i18nLanguage
}) => {
  const isNameHeuristic = mention.matchType === MentionMatchType.NameHeuristic;
  const isCollapsed = isNameHeuristic && 
    mention.userFeedback?.decision === MentionFeedbackDecision.Rejected && 
    !isExpanded;

  // State for context expansion
  const [contextExpanded, setContextExpanded] = useState(false);
  
  // Get truncated context
  const contextData = useMemo(() => {
    return truncateContext(mention.context, mention.matchedText);
  }, [mention.context, mention.matchedText]);

  const toggleContext = useCallback(() => {
    setContextExpanded(prev => !prev);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18nLanguage, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, [i18nLanguage]);

  const matchInfo = useMemo(() => {
    switch (mention.matchType) {
      case MentionMatchType.ExactId:
        return {
          label: t('mentions.matchTypes.exactId'),
          color: 'success' as const,
          icon: <Verified fontSize="small" />,
          description: t('mentions.matchTypes.exactIdDescription')
        };
      case MentionMatchType.NameHeuristic:
        return {
          label: t('mentions.matchTypes.possibleMatch'),
          color: 'warning' as const,
          icon: <Warning fontSize="small" />,
          description: t('mentions.matchTypes.possibleMatchDescription')
        };
      default:
        return {
          label: t('mentions.unknownMatchType'),
          color: 'default' as const,
          icon: <Info fontSize="small" />,
          description: t('mentions.unknownMatchTypeDescription')
        };
    }
  }, [mention.matchType, t]);


  const handleToggleExpansion = useCallback(() => {
    onToggleExpansion(mention.id);
  }, [onToggleExpansion, mention.id]);

  const handleFeedbackAccepted = useCallback(() => {
    onFeedback(mention.id, MentionFeedbackDecision.Accepted);
  }, [onFeedback, mention.id]);

  const handleFeedbackRejected = useCallback(() => {
    onFeedback(mention.id, MentionFeedbackDecision.Rejected);
  }, [onFeedback, mention.id]);

  const handleUndoFeedback = useCallback(() => {
    onUndoFeedback(mention.id);
  }, [onUndoFeedback, mention.id]);

  return (
    <Card sx={{ 
      mb: 2,
      // Gray out rejected mentions to make them less prominent
      ...(isCollapsed && {
        backgroundColor: 'grey.50',
        opacity: 0.7,
        '&:hover': {
          opacity: 0.85
        }
      })
    }}>
      <CardContent>
        {isCollapsed ? (
          // Collapsed state - simplified tabular view matching expanded layout
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'auto 1fr 1fr',
                md: 'auto 2fr 1fr'
              },
              gap: { xs: 2, sm: 3 },
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={handleToggleExpansion}
          >
            {/* Icon - grayed out */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {mention.nationalId.idType === IdType.PersonalId ? (
                <Person sx={{ fontSize: 32, color: 'action.disabled' }} />
              ) : (
                <Business sx={{ fontSize: 32, color: 'action.disabled' }} />
              )}
            </Box>
            
            {/* Title - grayed out */}
            <Typography 
              variant="h6" 
              component="h3" 
              id={`mention-${mention.id}-title`}
              sx={{ 
                fontWeight: 600,
                color: 'text.disabled',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {getNationalIdDisplayName(mention.nationalId, t)}
            </Typography>
            
            {/* Expand button - positioned to match third column space */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <IconButton
                ref={expandButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpansion();
                }}
                size="small"
                aria-label={t('mentions.expandDetails')}
                aria-describedby={`mention-${mention.id}-title`}
                aria-expanded={false}
                aria-controls={`mention-${mention.id}-content`}
              >
                <ExpandMore />
              </IconButton>
            </Box>
          </Box>
        ) : (
          // Expanded state - tabular row layout
          <Box>
            {/* Tabular row layout */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'auto 1fr 1fr',
                md: 'auto 2fr 1fr'
              },
              gap: { xs: 2, sm: 3 },
              alignItems: 'flex-start'
            }}>
              {/* Column 1: Icon - Person/Business with status color */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {mention.nationalId.idType === IdType.PersonalId ? (
                  <Person 
                    sx={{ 
                      fontSize: 32,
                      color: getIconColor(isCollapsed, mention.userFeedback, isNameHeuristic)
                    }} 
                  />
                ) : (
                  <Business 
                    sx={{ 
                      fontSize: 32,
                      color: getIconColor(isCollapsed, mention.userFeedback, isNameHeuristic)
                    }} 
                  />
                )}
              </Box>
              
              {/* Column 2: Title + Metadata */}
              <Box sx={{ minWidth: 0 }}>
                {/* Primary title: National ID name with warning icon */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                    {getNationalIdDisplayName(mention.nationalId, t)}
                  </Typography>
                  {/* Name heuristic warning icon with tooltip - next to name */}
                  {isNameHeuristic && (
                    <Tooltip title={matchInfo.description} placement="top" arrow>
                      <Warning color="warning" sx={{ fontSize: 18, cursor: 'help' }} />
                    </Tooltip>
                  )}
                  {/* Exact ID match icon with tooltip - next to name */}
                  {!isNameHeuristic && (
                    <Tooltip title={matchInfo.description} placement="top" arrow>
                      <Verified color="success" sx={{ fontSize: 18, cursor: 'help' }} />
                    </Tooltip>
                  )}
                </Box>
                
                {/* National ID info line */}
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                  {mention.nationalId ? (
                    <>
                      {mention.nationalId.idType === IdType.PersonalId ? t('registration.personalId') : t('registration.companyId')} • {formatNationalId(
                        mention.nationalId.normalizedId || '',
                        mention.nationalId.idType || IdType.PersonalId
                      )}
                    </>
                  ) : '--'}
                </Typography>

                {/* Collapsible context section */}
                {contextData.text && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.8125rem',
                      fontStyle: 'italic',
                      color: 'text.secondary'
                    }}>
                      "{contextExpanded ? mention.context : contextData.text}"
                    </Typography>
                    {contextData.wasTruncated && (
                      <Button 
                        size="small" 
                        onClick={toggleContext}
                        sx={{ 
                          p: 0, 
                          minWidth: 'auto', 
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          mt: 0.5
                        }}
                      >
                        {contextExpanded ? t('mentions.showLess') : t('mentions.showMore')}
                      </Button>
                    )}
                  </Box>
                )}

                {/* Found on timestamp */}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {t('mentions.details.foundOn')}: {formatDate(mention.createdAt)}
                </Typography>
              </Box>

              {/* Column 3: Source/Publication Info + Feedback */}
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flex: 1 }}>
                  {/* Publication title as link */}
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {mention.publication.sourceUrl ? (
                      <Link
                        href={formatSourceUrl(mention.publication, mention.pageNumber)}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        underline="hover"
                      >
                        {mention.publication.title}
                      </Link>
                    ) : (
                      mention.publication.title
                    )}
                  </Typography>

                  {/* Page link only - no duplicate info */}
                  {mention.pageNumber && (
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      display: 'block',
                      fontSize: '0.75rem',
                      mb: 2
                    }}>
                      <Link
                        href={formatSourceUrl(mention.publication, mention.pageNumber)}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                        underline="hover"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {t('mentions.details.viewPageLink', { pageNumber: mention.pageNumber })}
                      </Link>
                    </Typography>
                  )}
                </Box>

                {/* Feedback Section - positioned at bottom */}
                <Box sx={{ 
                  alignSelf: 'flex-end',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  mt: 'auto'
                }}>
                  {/* Monitored Identity Label - shown for all mentions if it exists */}
                  {mentionIdentity?.label && (
                    <Box sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: isNameHeuristic ? 1 : 0,
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50',
                      minHeight: '24px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      px: 1.5,
                      border: '1px solid',
                      textTransform: 'none',
                      fontWeight: 'normal'
                    }}>
                      <TagIcon sx={{ fontSize: '0.75rem !important' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {mentionIdentity.label}
                      </Typography>
                    </Box>
                  )}

                  {isNameHeuristic && (
                    <>
                      {mention.userFeedback ? (
                        // Show confirmation status with undo option - inline layout
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 1
                        }}>
                          {mention.userFeedback.decision === MentionFeedbackDecision.Accepted ? (
                            <ThumbUp fontSize="small" color="success" />
                          ) : (
                            <ThumbDown fontSize="small" color="action" />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.8125rem' }}>
                            {mention.userFeedback.decision === MentionFeedbackDecision.Accepted 
                              ? t('mentions.feedback.confirmed') 
                              : t('mentions.feedback.notMe')
                            }
                          </Typography>
                          <IconButton
                            onClick={handleUndoFeedback}
                            disabled={feedbackLoading}
                            size="small"
                            sx={{ 
                              cursor: feedbackLoading ? 'not-allowed' : 'pointer',
                              p: 0.5,
                              ml: 0.5
                            }}
                            aria-label={t('mentions.feedback.undoAccessibility')}
                          >
                            <Undo fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        // Show compact action buttons
                        <Stack 
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'flex-end' }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            startIcon={<ThumbUp />}
                            onClick={handleFeedbackAccepted}
                            disabled={feedbackLoading}
                            sx={{
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 1,
                              minWidth: 80
                            }}
                          >
                            {t('mentions.feedback.yesButton')}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<ThumbDown />}
                            onClick={handleFeedbackRejected}
                            disabled={feedbackLoading}
                            sx={{
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 1,
                              minWidth: 80
                            }}
                          >
                            {t('mentions.feedback.noButton')}
                          </Button>
                        </Stack>
                      )}

                      {/* Community feedback - more compact */}
                      {mention.feedbackSummary && mention.feedbackSummary.totalFeedbackCount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          fontStyle: 'italic', 
                          mt: 1,
                          fontSize: '0.6875rem',
                          textAlign: 'right'
                        }}>
                          {(() => {
                            const { feedbackSummary } = mention;
                            if (feedbackSummary.totalFeedbackCount === 0) {
                              return null;
                            }
                            
                            const otherAccepted = feedbackSummary.acceptedFeedbackCount;
                            const otherRejected = feedbackSummary.rejectedFeedbackCount;
                            
                            if (otherAccepted > 0 && otherRejected > 0) {
                              return t('mentions.feedback.communityMixed', { 
                                confirmed: otherAccepted, 
                                rejected: otherRejected 
                              });
                            } else if (otherAccepted > 0) {
                              return t('mentions.feedback.otherUsersConfirmed', { count: otherAccepted });
                            } else if (otherRejected > 0) {
                              return t('mentions.feedback.otherUsersRejected', { count: otherRejected });
                            }
                            return null;
                          })()}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

MentionCard.displayName = 'MentionCard';

// PropTypes validation for MentionCard component
(MentionCard as any).propTypes = {
  mention: PropTypes.shape({
    id: PropTypes.number.isRequired,
    nationalIdId: PropTypes.string.isRequired,
    publicationId: PropTypes.number.isRequired,
    matchType: PropTypes.oneOf([0, 1]).isRequired, // MentionMatchType enum values
    matchedText: PropTypes.string.isRequired,
    context: PropTypes.string,
    pageNumber: PropTypes.number,
    notificationSent: PropTypes.bool.isRequired,
    createdAt: PropTypes.string.isRequired,
    publication: PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      source: PropTypes.string.isRequired,
      sourceUrl: PropTypes.string,
      publicationDate: PropTypes.string.isRequired,
      referenceId: PropTypes.string.isRequired,
      documentSizeBytes: PropTypes.number,
      countryCode: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
    }).isRequired,
    nationalId: PropTypes.shape({
      id: PropTypes.string.isRequired,
      countryCode: PropTypes.string.isRequired,
      idType: PropTypes.oneOf([0, 1]).isRequired, // IdType enum values
      normalizedId: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      middleNames: PropTypes.string,
      lastName1: PropTypes.string,
      lastName2: PropTypes.string,
      fullName: PropTypes.string,
      createdAt: PropTypes.string.isRequired,
    }).isRequired,
    userFeedback: PropTypes.shape({
      id: PropTypes.string.isRequired,
      mentionId: PropTypes.number.isRequired,
      userId: PropTypes.number.isRequired,
      decision: PropTypes.oneOf([0, 1]).isRequired, // MentionFeedbackDecision enum values
      timestamp: PropTypes.string.isRequired,
    }),
    feedbackSummary: PropTypes.shape({
      totalFeedbackCount: PropTypes.number.isRequired,
      acceptedFeedbackCount: PropTypes.number.isRequired,
      rejectedFeedbackCount: PropTypes.number.isRequired,
      dominantFeedback: PropTypes.oneOf([0, 1]).isRequired,
    }),
  }).isRequired,
  mentionIdentity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    accountId: PropTypes.string,
    nationalIdId: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    isActive: PropTypes.bool.isRequired,
    createdAt: PropTypes.string.isRequired,
    nationalId: PropTypes.shape({
      id: PropTypes.string.isRequired,
      countryCode: PropTypes.string.isRequired,
      idType: PropTypes.oneOf([0, 1]).isRequired,
      normalizedId: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      middleNames: PropTypes.string,
      lastName1: PropTypes.string,
      lastName2: PropTypes.string,
      fullName: PropTypes.string,
      createdAt: PropTypes.string.isRequired,
    }),
    existingMentionCount: PropTypes.number,
  }),
  isExpanded: PropTypes.bool.isRequired,
  feedbackLoading: PropTypes.bool.isRequired,
  onToggleExpansion: PropTypes.func.isRequired,
  onFeedback: PropTypes.func.isRequired,
  onUndoFeedback: PropTypes.func.isRequired,
  expandButtonRef: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18nLanguage: PropTypes.string.isRequired,
};

const Mentions: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, token, authApi } = useAuth();
  const navigate = useNavigate();
  const [mentions, setMentions] = useState<MentionDto[]>([]);
  const [allMentions, setAllMentions] = useState<MentionDto[]>([]);
  const [monitoredIdentities, setMonitoredIdentities] = useState<MonitoredIdentityDto[]>([]);
  const [selectedIdentityId, setSelectedIdentityId] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState<Record<number, boolean>>({});
  const [expandedMentions, setExpandedMentions] = useState<Record<number, boolean>>({});
  const expandButtonRefs = useRef<Record<number, HTMLElement | null>>({});
  const [announcement, setAnnouncement] = useState<string>('');
  
  // Ref to track if component is mounted and for cleanup
  const isMountedRef = useRef(true);
  // Ref to track current request for cancellation
  const currentRequestRef = useRef<{ cancelled: boolean } | null>(null);

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (currentRequestRef.current) {
        currentRequestRef.current.cancelled = true;
      }
    };
  }, []);

  const fetchMentions = useCallback(async () => {
    if (!token) return;

    // Cancel any existing request
    if (currentRequestRef.current) {
      currentRequestRef.current.cancelled = true;
    }

    // Create new request tracker
    const requestTracker = { cancelled: false };
    currentRequestRef.current = requestTracker;
    
    try {
      setLoading(true);
      
      // Fetch monitored identities and mentions in parallel
      const [identitiesData, mentionsData] = await authApi(() =>
        Promise.all([
          monitoredIdentityService.getAll(token),
          monitoredIdentityService.getAllMentions(token)
        ])
      );

      // Check if this request was cancelled
      if (requestTracker.cancelled) {
        return;
      }
      
      // Feedback is now preloaded in the mentions data
      setMonitoredIdentities(identitiesData);
      setAllMentions(mentionsData || []);
      setMentions(mentionsData || []);
      
    } catch (error) {
      // Only handle error if request wasn't cancelled
      if (!requestTracker.cancelled) {
        setError(t('mentions.loadingError'));
        console.error('Error fetching mentions:', error);
      }
    } finally {
      // Only update loading state if this request wasn't cancelled
      if (!requestTracker.cancelled) {
        setLoading(false);
      }
    }
  }, [token, t, authApi]);

  useEffect(() => {
    if (isAuthenticated && user && token) {
      fetchMentions();
    }
  }, [isAuthenticated, user, token, fetchMentions]);

  // Filter mentions based on selected identity
  useEffect(() => {
    let isMounted = true;
    let delayTimeout: NodeJS.Timeout;
    let errorTimeout: NodeJS.Timeout;

    const applyFilter = async () => {
      if (!isMounted) return;
      setFilterLoading(true);
      
      // Add small delay to show loading state for quick operations
      await new Promise(resolve => {
        delayTimeout = setTimeout(resolve, 150);
      });
      
      if (!isMounted) return;
      
      if (selectedIdentityId === 'all') {
        setMentions(allMentions);
      } else {
        // Find the selected monitored identity
        const selectedIdentity = monitoredIdentities.find(identity => identity.id === selectedIdentityId);
        if (selectedIdentity) {
          // Filter mentions by matching national ID with null checks
          const filtered = allMentions.filter(mention => {
            // Add null checks for mention.nationalIdId to prevent runtime errors
            if (!mention.nationalIdId) {
              console.warn('Mention found with missing nationalIdId:', mention);
              return false;
            }
            return mention.nationalIdId === selectedIdentity.nationalIdId;
          });
          setMentions(filtered);
        } else {
          // Handle case where selectedIdentityId refers to a non-existent identity
          if (monitoredIdentities.length > 0) {
            console.warn('Selected identity not found, resetting filter to "all":', selectedIdentityId);
            setSelectedIdentityId('all');
            setMentions(allMentions); // Show all mentions when resetting
            setError(t('mentions.identityNotFound'));
            // Clear error after a delay
            errorTimeout = setTimeout(() => {
              if (isMounted) {
                setError(null);
              }
            }, 5000);
          } else {
            // No identities loaded yet, show empty list
            setMentions([]);
          }
        }
      }
      
      if (isMounted) {
        setFilterLoading(false);
      }
    };
    
    if (!loading) {
      applyFilter();
    }

    return () => {
      isMounted = false;
      if (delayTimeout) {
        clearTimeout(delayTimeout);
      }
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [selectedIdentityId, allMentions, monitoredIdentities, t, loading]);

  const getIdentityDisplayName = useCallback((identity: MonitoredIdentityDto) => {
    if (identity.fullName) {
      return identity.fullName;
    }
    
    if (identity.nationalId?.fullName) {
      return identity.nationalId.fullName;
    }
    
    const parts = [
      identity.nationalId?.firstName,
      identity.nationalId?.middleNames,
      identity.nationalId?.lastName1,
      identity.nationalId?.lastName2
    ].filter(Boolean);
    
    return parts.length > 0 ? parts.join(' ') : identity.nationalId?.normalizedId || identity.nationalIdId;
  }, []);

  // Sort monitored identities: personal IDs first, then company IDs, each group sorted by name
  const sortedMonitoredIdentities = useMemo(() => {
    return [...monitoredIdentities].sort((a, b) => {
      // First, sort by ID type (PersonalId = 0 comes before CompanyId = 1)
      const typeA = a.nationalId?.idType ?? IdType.PersonalId;
      const typeB = b.nationalId?.idType ?? IdType.PersonalId;
      
      if (typeA !== typeB) {
        return typeA - typeB;
      }
      
      // Within same type, sort by display name ascending
      const nameA = getIdentityDisplayName(a).toLowerCase();
      const nameB = getIdentityDisplayName(b).toLowerCase();
      
      return nameA.localeCompare(nameB);
    });
  }, [monitoredIdentities, getIdentityDisplayName]);

  const handleIdentityFilterChange = (identityId: string) => {
    setSelectedIdentityId(identityId);
    
    // Announce filter change
    const identity = monitoredIdentities.find(id => id.id === identityId);
    if (identityId === 'all') {
      setAnnouncement(t('mentions.accessibility.filteredAll', { total: allMentions.length }));
    } else if (identity) {
      const identityName = getIdentityDisplayName(identity);
      setAnnouncement(t('mentions.accessibility.filteredBy', { name: identityName }));
    }
  };




  const getIdentityForMention = useCallback((mention: MentionDto) => {
    // Add null checks for mention.nationalId to prevent runtime errors
    if (!mention.nationalId || !mention.nationalIdId) {
      console.warn('Mention found with missing nationalId data:', mention);
      return null;
    }
    
    return monitoredIdentities.find(identity => 
      identity.nationalIdId === mention.nationalIdId
    ) || null;
  }, [monitoredIdentities]);

  const handleFeedback = useCallback(async (mentionId: number, decision: MentionFeedbackDecision) => {
    if (!token) return;
    
    try {
      setFeedbackLoading(prev => ({ ...prev, [mentionId]: true }));
      
      const feedback = await authApi(() => mentionService.provideFeedback(mentionId, { decision }, token));
      
      // Update the mention in both state arrays
      const updateMention = (prevMentions: MentionDto[]) => 
        prevMentions.map(mention => 
          mention.id === mentionId 
            ? { ...mention, userFeedback: feedback }
            : mention
        );
      
      setMentions(updateMention);
      setAllMentions(updateMention);
    } catch (error) {
      console.error('Error providing feedback:', error);
      // You could show an error message here
    } finally {
      setFeedbackLoading(prev => ({ ...prev, [mentionId]: false }));
    }
  }, [token, authApi]);

  const handleUndoFeedback = useCallback(async (mentionId: number) => {
    if (!token) return;
    
    try {
      setFeedbackLoading(prev => ({ ...prev, [mentionId]: true }));
      
      await authApi(() => mentionService.deleteFeedback(mentionId, token));
      
      // Update the mention in both state arrays to remove feedback
      const updateMention = (prevMentions: MentionDto[]) => 
        prevMentions.map(mention => 
          mention.id === mentionId 
            ? { ...mention, userFeedback: undefined }
            : mention
        );
      
      setMentions(updateMention);
      setAllMentions(updateMention);
    } catch (error) {
      console.error('Error undoing feedback:', error);
      // You could show an error message here
    } finally {
      setFeedbackLoading(prev => ({ ...prev, [mentionId]: false }));
    }
  }, [token, authApi]);

  const toggleMentionExpansion = useCallback((mentionId: number) => {
    const wasCollapsed = !expandedMentions[mentionId];
    const mention = mentions.find(m => m.id === mentionId);
    
    setExpandedMentions(prev => ({
      ...prev,
      [mentionId]: !prev[mentionId]
    }));
    
    // Screen reader announcement
    if (mention) {
      if (wasCollapsed) {
        setAnnouncement(t('mentions.accessibility.expandedDetails', { title: mention.publication.title }));
      } else {
        setAnnouncement(t('mentions.accessibility.collapsedDetails', { title: mention.publication.title }));
      }
    }
    
    // Focus management: when expanding, focus moves to the content area
    // When collapsing, focus returns to the expand button
    if (wasCollapsed) {
      // Expanding - focus will be managed by the content becoming visible
      setTimeout(() => {
        const contentElement = document.getElementById(`mention-${mentionId}-content`);
        if (contentElement) {
          contentElement.focus();
        }
      }, 150); // Wait for transition
    } else {
      // Collapsing - focus the expand button if it exists
      setTimeout(() => {
        const expandButton = expandButtonRefs.current[mentionId];
        if (expandButton) {
          expandButton.focus();
        }
      }, 150);
    }
  }, [expandedMentions, mentions, t]);


  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          {t('auth.needToRegister')}
          <Button onClick={() => {
            const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
            const homeRoute = t('routes.home');
            navigate(`/${currentLanguage}${homeRoute}`);
          }} sx={{ ml: 2 }}>
            {t('auth.registerNow')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {/* Screen reader announcements */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {announcement}
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('mentions.title')}
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('mentions.welcome', { name: user?.fullName || user?.email })}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('mentions.description')}
          </Typography>
        </CardContent>
      </Card>

      {/* Filter Controls */}
      {monitoredIdentities.length > 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ 
              display: 'flex', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                <FilterList color="primary" />
                <FormControl sx={{ 
                  minWidth: { xs: 120, sm: 200 },
                  flex: { xs: 1, sm: 'none' }
                }}>
                  <InputLabel>{t('mentions.filterByIdentity')}</InputLabel>
                  <Select
                    value={selectedIdentityId}
                    label={t('mentions.filterByIdentity')}
                    onChange={(e) => handleIdentityFilterChange(e.target.value)}
                    disabled={filterLoading}
                    aria-label={t('mentions.filterByIdentity')}
                    aria-describedby="filter-description"
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterList fontSize="small" />
                        <Box component="span" sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {t('mentions.allIdentities')}
                        </Box>
                      </Box>
                    </MenuItem>
                    {sortedMonitoredIdentities.map((identity) => (
                      <MenuItem key={identity.id} value={identity.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                          {identity.nationalId?.idType === IdType.PersonalId ? (
                            <Person fontSize="small" />
                          ) : (
                            <Business fontSize="small" />
                          )}
                          <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flex: 1,
                            minWidth: 0
                          }}>
                            <Box component="span" sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {(() => {
                                const displayName = getIdentityDisplayName(identity);
                                return displayName.length > 80 
                                  ? `${displayName.substring(0, 77)}...`
                                  : displayName;
                              })()}
                            </Box>
                            {identity.label && (
                              <Box sx={{ 
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.25,
                                color: 'primary.main',
                                borderColor: 'primary.main',
                                backgroundColor: 'primary.50',
                                minHeight: '20px',
                                borderRadius: '10px',
                                fontSize: '0.6875rem',
                                px: 0.75,
                                border: '1px solid',
                                textTransform: 'none',
                                fontWeight: 'normal',
                                flexShrink: 0
                              }}>
                                <TagIcon sx={{ fontSize: '0.6875rem !important' }} />
                                <Typography variant="caption" sx={{ fontSize: '0.6875rem' }}>
                                  {identity.label.length > 20 
                                    ? `${identity.label.substring(0, 17)}...`
                                    : identity.label
                                  }
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              {filterLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress 
                    size={16} 
                    aria-label={t('mentions.filteringInProgress')}
                    role="progressbar"
                    aria-describedby="filter-loading-text"
                  />
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    id="filter-loading-text"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {t('mentions.filtering')}
                  </Typography>
                </Box>
              ) : (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  id="filter-description"
                  aria-live="polite"
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {t('mentions.showingCount', { count: mentions.length, total: allMentions.length })}
                  </Box>
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    {t('mentions.showingCount', { count: mentions.length, total: allMentions.length })}
                  </Box>
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
          <CircularProgress 
            size={40}
            aria-label={t('mentions.loadingMentions')}
            role="progressbar"
            aria-describedby="loading-message"
          />
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mt: 2 }}
            id="loading-message"
          >
            {t('mentions.loadingMessage')}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !filterLoading && mentions.length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('mentions.noMentionsFound')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('mentions.noMentionsDescription')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {filterLoading && mentions.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
          <CircularProgress 
            size={40}
            aria-label={t('mentions.filteringInProgress')}
            role="progressbar"
            aria-describedby="filter-loading-message"
          />
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mt: 2 }}
            id="filter-loading-message"
          >
            {t('mentions.applyingFilter')}
          </Typography>
        </Box>
      )}

      {mentions.map((mention) => (
        <MentionCard
          key={mention.id}
          mention={mention}
          mentionIdentity={getIdentityForMention(mention)}
          isExpanded={!!expandedMentions[mention.id]}
          feedbackLoading={!!feedbackLoading[mention.id]}
          onToggleExpansion={toggleMentionExpansion}
          onFeedback={handleFeedback}
          onUndoFeedback={handleUndoFeedback}
          expandButtonRef={(el) => { expandButtonRefs.current[mention.id] = el; }}
          t={t}
          i18nLanguage={i18n.language}
        />
      ))}
    </Container>
  );
};

export default Mentions;